import { IS_SSR, React } from '@/config'
import { onUpdate } from '@codeleap/hooks'
import { Icon, View, ActivityIndicator } from '@/components'
import { Logo } from './Logo'
import { useAnimatedStyle } from '@codeleap/web'
import { createStyles } from '@codeleap/styles'
import { appStatusStore } from '@/stores'
import { memoize } from '@codeleap/utils'
import ReactDOM from 'react-dom'

const indicatorSize = 60

const AppStatusOverlayComponent = () => {
  const status = appStatusStore.state.use(state => state.status)

  onUpdate(() => {
    if (status === 'done') {
      setTimeout(() => {
        appStatusStore.set('idle')
      }, 2000)
    }
  }, [status])

  const visibilityStyle = React.useCallback((appStatus) => {
    const isStatusVisible = status === appStatus

    return ({
      transform: `scale(${isStatusVisible ? 1 : 0})`,
      transition: 'transform 0.3s ease',
    })
  }, [status])

  const visibleOverlay = ['loading', 'done'].includes(status)

  const hide = ['blank', 'idle'].includes(status)

  const logoAnimation = useAnimatedStyle(() => ({
    scale: status == 'splash' ? 1 : 0.2,
    opacity: status == 'splash' ? 1 : 0.5,
    transition: {
      duration: .15,
    },
  }), [status])

  return (
    <>
      <View style={[styles.wrapper, visibleOverlay && styles['wrapper:visible'], { pointerEvents: hide ? 'none' : 'auto' }]}>
        <View style={styles.overlay}>
          <View style={[styles.container, visibilityStyle('done')]}>
            <Icon
              debugName='AppStatus:done'
              name='check'
              style={['size:4', visibilityStyle('done')]}
            />
          </View>
        </View>

        <View style={styles.overlay}>
          <View style={[styles.container, visibilityStyle('loading')]}>
            <ActivityIndicator debugName='AppStatus:indicator' style={visibilityStyle('loading')} />
          </View>
        </View>
      </View>

      <View style={[styles.splashWrapper, { opacity: status === 'splash' ? 1 : 0 }]}>
        <Logo
          debugName='AppStatus:splash'
          style={'splash'}
          responsive={false}
          animated
          component={View}
          animatedProps={logoAnimation}
        />
      </View>
    </>
  )
}

export const AppStatusOverlay = memoize(() => {
  if (IS_SSR) return <AppStatusOverlayComponent />
  return ReactDOM.createPortal(<AppStatusOverlayComponent />, document.getElementById('app-status-portal'))
})

const styles = createStyles((theme) => ({
  overlay: {
    ...theme.presets.full,
    ...theme.presets.fixed,
    ...theme.presets.whole,
    ...theme.presets.center,
    zIndex: 9999,
  },
  container: {
    width: indicatorSize,
    height: indicatorSize,
    backgroundColor: theme.baseColors.neutralSolid0,
    borderRadius: theme.radius.infinite,
    ...theme.presets.center,
    zIndex: 9999,
  },
  wrapper: {
    ...theme.presets.fixed,
    ...theme.presets.whole,
    ...theme.presets.justifyCenter,
    ...theme.presets.alignCenter,
    zIndex: 99999,
    pointerEvents: 'none',
    transition: 'all 0.3s ease',
  },
  'wrapper:visible': {
    backgroundColor: theme.baseColors.neutralBlackTransparent500,
  },
  splashWrapper: {
    display: 'flex',
    ...theme.presets.fixed,
    ...theme.presets.whole,
    ...theme.presets.justifyCenter,
    ...theme.presets.alignCenter,
    zIndex: 99999,
    pointerEvents: 'none',
    transition: 'all 0.3s ease',
    backgroundColor: theme.originalBaseColors.primarySolid500,
  },
}))
